import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import {
  fetchTaskSearchLabelGroup,
  fetchTaskLabelingConditionNew,
  fetchTaskMemberLabelingConditionNew,
} from '../../../states/containers/task/taskSlice'
import { convertHexColor } from '../../../utils/color'
import Modal from '../common/Modal'
import Tips from '../common/Tips'
import AddLabelBigModal from './AddLabelBigModal'
import FilePathCondition from './FilePathCondition'
import KeywordCondition from './KeywordCondition'
import SelectMemberModule from '../search/SelectMemberModule'
import ServiceCondition from './ServiceCondition'
import TeamAndMembersCondition from './TeamAndMembersCondition'
import UrlCondition from './UrlCondition'
import AutoLabelingTargetPreview, { MatchableTask } from './AutoLabelingTargetPreview'

type SelectListValue = 'service' | 'keyword' | 'file_path' | 'url' | 'team_and_members'

type LabelingTarget = {
  labelGroupId: number
  labelGroupName: string
  labelGroupCode: string | null
  labelGroupColor: string
  labelId: number
  labelName: string
  labelCode: string | null
  labelColor: string
}

type LabelingConditionFields = {
  services: {
    id: number
    name: string
  }[]
  keyword: {
    operator: string | null
    words: string[]
  }
  filePaths: string[]
  url: {
    pattern: string | null
    words: string[]
  }
  teams: {
    id: number
    name: string
  }[]
  members: {
    id: number
    name: string
  }[]
}

type SelectableMember = {
  id: number
  name: string
  profile_image?: string | null
  code?: string | null
  kana?: string | null
}

export type LabelingConditionSubmit = {
  labelingTarget: LabelingTarget
  member?: SelectableMember
  matchedPreviewTaskIds?: number[]
} & LabelingConditionFields

export type CandidateLabelings = {
  labelGroupId: number
  labelGroupName: string
  labelGroupCode: string | null
  labelGroupColor: string
  disabled: boolean
  labels: {
    labelId: number
    labelName: string
    labelCode: string | null
    labelColor: string
    description: string | null
    disabled: boolean
  }[]
}[]

export type SuggestionKey = Exclude<SelectListValue, 'team_and_members'>
// export type ConditionSuggestions = Partial<Record<SuggestionKey, string[]>>

export type ConditionSuggestions = {
  services: {
    id: number
    name: string
  }[]
  keyword: string[]
  file_path: string[]
  url: string[]
}

type ModalState = {
  initialLabelingCondition?: {
    labelingTarget?: LabelingTarget
  } & LabelingConditionFields
  candidateMembers: { id: number, name: string }[]
  candidateTeams: { id: number, name: string }[]
  candidateServices: { id: number, name: string }[]
  isCandidateServicesLoading: boolean
  candidateLabelings: CandidateLabelings
  candidateSelectableMembers?: SelectableMember[]
  initialMember?: SelectableMember
  conditionSuggestions?: ConditionSuggestions
  taskId?: number
}

type LabelingConditionModalProps = {
  isDisplay: boolean
  mode: 'create' | 'edit'
  variant?: 'member' | 'project'
  modalState: ModalState
  lockMember?: boolean
  previewTasks?: MatchableTask[]
  onCancelCallback: VoidFunction
  onSubmitCallback: (submitData: LabelingConditionSubmit) => void
}

const SELECT_LIST = [
  {
    value: "team_and_members",
    label: "チーム・ユーザー",
    color: "#4472ba",
    icon: "users",
  },
  {
    value: "service",
    label: "アプリケーション",
    color: "#ba5a44",
    icon: "star",
  },
  {
    value: "keyword",
    label: "キーワード",
    color: "#baa744",
    icon: "pen",
  },
  {
    value: "url",
    label: "URL",
    color: "#209a48",
    icon: "window-maximize",
  },
  {
    value: "file_path",
    label: "ファイルパス",
    color: "#8c30a7",
    icon: "folder",
  },
] as {
  value: SelectListValue
  label: string
  color: string
  icon: IconName
}[]

const IMITATION = [
  "Chrome",
  "Edge",
  "Firefox",
]

const LabelingConditionModal: FC<LabelingConditionModalProps> = (props) => {
  const {
    isDisplay,
    mode,
    variant = 'project',
    modalState,
    lockMember = false,
    previewTasks,
    onCancelCallback,
    onSubmitCallback,
  } = props

  const isMemberVariant = variant === 'member'

  const dispatch = useDispatch()
  const login = useSelector((state: RootState) => state.login)

  const [memberCandidateLabelings, setMemberCandidateLabelings] = useState<CandidateLabelings>([])
  const [selectedMember, setSelectedMember] = useState<SelectableMember | null>(null)
  const [memberErrors, setMemberErrors] = useState<string[]>([])

  const [labelingTarget, setLabelingTarget] = useState<LabelingTarget | null>(null)
  const [labelingTargetErrors, setLabelingTargetErrors] = useState<string[]>([])

  const [participatingTeamIds, setParticipatingTeamIds] = useState<number[] | null>(null)
  const [participatingMemberIds, setParticipatingMemberIds] = useState<number[] | null>(null)

  const [loadingCount, setLoadingCount] = useState<number>(0)

  // NOTE: 各仕分け条件の追加順を保持するだけのリスト。実際の入力値は各Stateで管理している
  const [selectListItems, setSelectListItems] = useState<SelectListValue[]>([])
  const [teams, setTeams] = useState<{ id: number, name: string }[]>([])
  const [members, setMembers] = useState<{ id: number, name: string }[]>([])
  const [services, setServices] = useState<{ id: number, name: string }[]>([])
  const [keywordWords, setKeywordWords] = useState<string[]>([])
  const [keywordOperator, setKeywordOperator] = useState<string>("or")
  const [urlWords, setUrlWords] = useState<string[]>([])
  const [urlPattern, setUrlPattern] = useState<string>("partial")
  const [filePaths, setFilePaths] = useState<string[]>([])
  const [labelingConditionDetailErrors, setLabelingConditionDetailErrors] = useState<string[]>([])

  // 条件タイプごとの入力候補(チップ表示用)
  const [serviceSuggestions, setServiceSuggestions] = useState<{ id: number, name: string }[]>([])
  const [keywordSuggestions, setKeywordSuggestions] = useState<string[]>([])
  const [urlSuggestions, setUrlSuggestions] = useState<string[]>([])
  const [filePathSuggestions, setFilePathSuggestions] = useState<string[]>([])

  const [isDisplayTargetSelect, setIsDisplayTargetSelect] = useState<boolean>(false)
  const [isDisplayTypeSelect, setIsDisplayTypeSelect] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [matchedPreviewCount, setMatchedPreviewCount] = useState(0)

  const skipValidationRef = useRef(false)
  const matchedPreviewTaskIdsRef = useRef<number[]>([])

  // API

  const loadMemberLabelingTargetCandidates = (memberId: number): void => {
    setLoadingCount((count) => count + 1)
    dispatch(fetchTaskSearchLabelGroup(
      { member_id: memberId },
      (result) => {
        setMemberCandidateLabelings(result.label_groups.map((labelGroup) => ({
          labelGroupId: labelGroup.label_group_id,
          labelGroupName: labelGroup.label_group_name,
          labelGroupCode: labelGroup.label_group_code,
          labelGroupColor: labelGroup.label_group_color,
          disabled: labelGroup.disabled,
          labels: labelGroup.labels.map((label) => ({
            labelId: label.label_id,
            labelName: label.label_name,
            labelCode: label.label_code,
            labelColor: label.label_color,
            description: label.description,
            disabled: label.disabled,
          })),
        })))
      },
      () => setLoadingCount((count) => count - 1),
    ))
  }

  // member variant: 起動元タスクからキーワードのサジェスト候補を取得する。
  // 取得できたらキーワード条件の行を表示し、サジェストを差し込む。
  const loadKeywordSuggestions = (taskId: number): void => {
    setLoadingCount((count) => count + 1)
    dispatch(fetchTaskMemberLabelingConditionNew(
      { task_id: taskId },
      (result) => {
        setKeywordSuggestions(result.suggestion_keywords)
        if (result.suggestion_keywords.length) {
          setSelectListItems((prev) => (prev.includes("keyword") ? prev : [...prev, "keyword"]))
        }
        if (result.suggestion_services.length) {
          setServiceSuggestions(result.suggestion_services)
          setServices((prev) => (prev.length ? prev : [result.suggestion_services[0]]))
          setSelectListItems((prev) => (prev.includes("service") ? prev : [...prev, "service"]))
        }
      },
      () => setLoadingCount((count) => count - 1),
    ))
  }

  // handler

  const selectMember = (member: SelectableMember): void => {
    setSelectedMember(member)
    setMemberErrors([])
    setLabelingTarget(null)
    setLabelingTargetErrors([])
    loadMemberLabelingTargetCandidates(member.id)
  }

  const addSelectItem = (itemType: SelectListValue) => {
    setIsDisplayTypeSelect(false)
    setSelectListItems((prev) => [...prev, itemType])
  }

  const clearCondition = (index: number) => {
    const targetItem = selectListItems[index]
    if (targetItem === "team_and_members") {
      setTeams([])
      setMembers([])
    } else if (targetItem === "service") {
      setServices([])
    } else if (targetItem === "keyword") {
      setKeywordWords([])
      setKeywordOperator("or")
    } else if (targetItem === "url") {
      setUrlWords([])
      setUrlPattern("partial")
    } else if (targetItem === "file_path") {
      setFilePaths([])
    }
  }

  const deleteItem = (index: number) => {
    clearCondition(index)
    setSelectListItems((prev) => prev.filter((_v, i) => i !== index))
  }

  // validation

  const hasValidationError = (): boolean => (
    !!memberErrors.length
    || !!labelingTargetErrors.length
    || !!labelingConditionDetailErrors.length
  )

  const validateMember = (): boolean => {
    if (!isMemberVariant) return true
    if (!selectedMember) {
      setMemberErrors(['ユーザーが設定されていません'])
      return false
    }
    setMemberErrors([])
    return true
  }

  const validateLabelingTarget = (): boolean => {
    if (!labelingTarget) {
      setLabelingTargetErrors(['仕分け先が設定されていません'])
      return false
    }
    setLabelingTargetErrors([])
    return true
  }

  const validateConditionDetails = (): boolean => {
    const errors: string[] = []
    if (!selectListItems.length) {
      errors.push("自動仕分け条件が設定されていません")
    }
    const hasAnyConditionValue = teams.length || members.length || services.length || keywordWords.length || urlWords.length || filePaths.length
    if (selectListItems.length && !hasAnyConditionValue) {
      errors.push("仕分け条件の入力値が全て設定されていません")
    }
    const hasExcludedTeam = participatingTeamIds !== null && teams.some((team) => !participatingTeamIds.includes(team.id))
    if (selectListItems.includes('team_and_members') && hasExcludedTeam) {
      errors.push("仕分け先プロジェクトの参加チームに含まれないチームが設定されています")
    }
    const hasExcludedMember = participatingMemberIds !== null && members.some((member) => !participatingMemberIds.includes(member.id))
    if (selectListItems.includes('team_and_members') && hasExcludedMember) {
      errors.push("仕分け先プロジェクトの参加ユーザーに含まれないユーザーが設定されています")
    }
    if (selectListItems.includes('service') && services.some((w) => IMITATION.includes(w.name))) {
      errors.push("Webブラウザーをアプリケーションとして設定できません")
    }
    if (selectListItems.includes('url') && !services.length) {
      errors.push("URLを設定する場合、アプリケーションの設定も必須です")
    }
    setLabelingConditionDetailErrors(errors)
    return errors.length === 0
  }

  const validate = (): boolean => {
    // NOTE: 各validateXxxxは副作用を持つため、ショートサーキットさせずに全て実行する
    const results = [
      validateMember(),
      validateLabelingTarget(),
      validateConditionDetails(),
    ]
    return results.every(Boolean)
  }

  // action

  const submit = () => {
    setIsSubmitting(true)
    const isValid = validate()
    if (!isValid) {
      setIsSubmitting(false)
      return
    }
    // validate通過時点で存在するはず(型ガード節)
    if (!labelingTarget) {
      setIsSubmitting(false)
      return
    }
    onSubmitCallback({
      labelingTarget,
      member: isMemberVariant ? (selectedMember ?? undefined) : undefined,
      matchedPreviewTaskIds: matchedPreviewTaskIdsRef.current,
      services,
      keyword: { operator: keywordOperator, words: keywordWords },
      filePaths,
      url: { pattern: urlPattern, words: urlWords },
      teams,
      members,
    })
  }

  // calculated

  const isConditionLoading = loadingCount > 0

  // NOTE: 仕分け先候補の決定, high_volume時は仕分け先を選択するモーダル内部で決定する
  const effectiveCandidateLabelings = isMemberVariant ? memberCandidateLabelings : modalState.candidateLabelings

  const labelGroupOriginList = useMemo(() => effectiveCandidateLabelings.map((labelGroup) => ({
    label_group_id: labelGroup.labelGroupId,
    label_group_name: labelGroup.labelGroupName,
    label_group_color: labelGroup.labelGroupColor,
    label_group_code: labelGroup.labelGroupCode,
    disabled: labelGroup.disabled,
    labels: labelGroup.labels.map((label) => ({
      label_id: label.labelId,
      label_name: label.labelName,
      label_color: label.labelColor,
      label_code: label.labelCode,
      description: label.description,
      disabled: label.disabled,
    })),
  })), [effectiveCandidateLabelings])

  const availableSelectList = isMemberVariant
    ? SELECT_LIST.filter((v) => v.value !== "team_and_members")
    : SELECT_LIST

  // useEffect

  useEffect(() => {
    if (!isDisplay) return
    skipValidationRef.current = true
    const initial = modalState.initialLabelingCondition ?? {
      labelingTarget: undefined,
      services: [],
      keyword: { operator: null, words: [] },
      filePaths: [],
      url: { pattern: null, words: [] },
      teams: [],
      members: [],
    }
    const sugg = modalState.conditionSuggestions

    setLabelingTarget(initial.labelingTarget ?? null)
    setLabelingTargetErrors([])
    setParticipatingTeamIds(null)
    setParticipatingMemberIds(null)
    setLoadingCount(0)
    setLabelingConditionDetailErrors([])

    setTeams(initial.teams)
    setMembers(initial.members)
    setKeywordOperator(initial.keyword.operator ?? "or")
    setKeywordWords(initial.keyword.words)
    setUrlPattern(initial.url.pattern ?? "partial")
    setUrlWords(initial.url.words)
    setFilePaths(initial.filePaths)
    // NOTE: service のみ、初期値が無くてもサジェスト先頭をデフォルト入力しておく
    if (initial.services.length) {
      setServices(initial.services)
    } else {
      setServices(sugg?.services?.length ? [sugg.services[0]] : [])
    }

    setServiceSuggestions(sugg?.services ?? [])
    setKeywordSuggestions(sugg?.keyword ?? [])
    setUrlSuggestions(sugg?.url ?? [])
    setFilePathSuggestions(sugg?.file_path ?? [])

    const initialItems: SelectListValue[] = []
    if (initial.teams.length || initial.members.length) initialItems.push("team_and_members")
    if (initial.services.length || sugg?.services?.length) initialItems.push("service")
    if (initial.keyword.words.length || sugg?.keyword?.length) initialItems.push("keyword")
    if (initial.url.words.length || sugg?.url?.length) initialItems.push("url")
    if (initial.filePaths.length || sugg?.file_path?.length) initialItems.push("file_path")
    setSelectListItems(initialItems)

    const initialMember = modalState.initialMember ?? null
    setSelectedMember(initialMember)
    setMemberErrors([])
    setMemberCandidateLabelings([])
    if (isMemberVariant && initialMember) {
      loadMemberLabelingTargetCandidates(initialMember.id)
    }
    if (isMemberVariant && modalState.taskId) {
      loadKeywordSuggestions(modalState.taskId)
    }

    setIsDisplayTypeSelect(false)
    setIsDisplayTargetSelect(false)
    setIsSubmitting(false)
  }, [isDisplay])

  useEffect(() => {
    if (skipValidationRef.current) {
      skipValidationRef.current = false
      return
    }
    validateConditionDetails()
  }, [selectListItems, teams, members, keywordWords, urlWords, filePaths, services, participatingTeamIds, participatingMemberIds])

  useEffect(() => {
    if (!isDisplay) return
    if (isMemberVariant) return
    if (!labelingTarget) {
      setParticipatingTeamIds(null)
      setParticipatingMemberIds(null)
      return
    }
    setLoadingCount((count) => count + 1)
    dispatch(fetchTaskLabelingConditionNew(
      { label_group_id: labelingTarget.labelGroupId },
      (result) => {
        setParticipatingTeamIds(result.candidate_teams.map((team) => team.id))
        setParticipatingMemberIds(result.candidate_members.map((member) => member.id))
      },
      () => setLoadingCount((count) => count - 1),
    ))
  }, [isDisplay, isMemberVariant, labelingTarget?.labelGroupId])

  return (
    <>
      <Modal
        title={mode === 'edit' ? "自動仕分け設定の編集" : "自動仕分け設定の作成"}
        show={isDisplay}
        wrapperClass="labeling_condition_modal"
        closeCallback={onCancelCallback}
        closeable
      >
        <>
          <div className="item" style={{ color: '#666666' }}>
            自動仕分けの優先度については「
            <a href="https://qasee.zendesk.com/hc/ja/articles/59236827027993" target="_blank" rel="noreferrer">自動仕分け条件の適用優先度について</a>
            &nbsp;
            <FontAwesomeIcon icon={['fas', 'external-link-alt']} style={{ fontSize: '9px' }} />
            」をご覧ください。
          </div>
          {
            isMemberVariant && (
              <div className="item">
                <div className="item_header">
                  <div className="title">
                    ユーザー
                  </div>
                </div>
                <div className="detail row_1">
                  <SelectMemberModule
                    selectedMember={selectedMember}
                    memberList={modalState.candidateSelectableMembers ?? []}
                    callbackMember={(member) => selectMember(member)}
                    disabled={lockMember}
                  />
                  {
                    !!memberErrors.length && (
                      <div className="error">
                        {memberErrors.join(", ")}
                      </div>
                    )
                  }
                </div>
              </div>
            )
          }
          <div className="item">
            <div className="item_header">
              <div className="title">
                仕分け先
              </div>
            </div>
            <div className="detail row_1">
              {
                labelingTarget ? (
                  <button
                    type="button"
                    className="target_preview"
                    onClick={() => setIsDisplayTargetSelect(true)}
                  >
                    <div className="target_chip">
                      <div className="target_item">
                        <div
                          className="target_color"
                          style={{ backgroundColor: labelingTarget.labelGroupColor }}
                        />
                        {labelingTarget.labelGroupName}
                        {!!labelingTarget.labelGroupCode && ` (${labelingTarget.labelGroupCode})`}
                      </div>
                      <FontAwesomeIcon icon={['fas', 'angle-right']} className="target_separator" />
                      <div className="target_item">
                        <div
                          className="target_color round"
                          style={{ backgroundColor: labelingTarget.labelColor }}
                        />
                        {labelingTarget.labelName}
                        {!!labelingTarget.labelCode && ` (${labelingTarget.labelCode})`}
                      </div>
                    </div>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="target_preview"
                    onClick={() => setIsDisplayTargetSelect(true)}
                  >
                    <div className="target_chip">
                      仕分け先を選択
                    </div>
                  </button>
                )
              }
              {
                !!labelingTargetErrors.length && (
                  <div className="error">
                    {labelingTargetErrors.join(", ")}
                  </div>
                )
              }
            </div>
          </div>
          <div className="item">
            <div className="item_header">
              <div className="title">
                自動仕分け条件
              </div>
            </div>
            <div className="condition_area">
              <div className="condition_list">
                {
                  selectListItems.map((item, index) => {
                    const targetItem = SELECT_LIST.find((w) => w.value === item)
                    if (!targetItem) return (<></>)
                    const rowBackground = convertHexColor(targetItem?.color || "#E9EEF0", 0.1)
                    return (
                      <React.Fragment key={`condition_${item}`}>
                        {
                          !!index && (
                            <div className="and_divider">
                              AND
                            </div>
                          )
                        }
                        <div
                          className="condition_row"
                          style={{ backgroundColor: rowBackground }}
                        >
                          <div className="condition_row_body">
                            {
                              item === "team_and_members" && (
                                <TeamAndMembersCondition
                                  targetItem={targetItem}
                                  teams={teams}
                                  members={members}
                                  teamCandidates={modalState.candidateTeams}
                                  memberCandidates={modalState.candidateMembers}
                                  participatingTeamIds={participatingTeamIds}
                                  participatingMemberIds={participatingMemberIds}
                                  onTeamsChange={setTeams}
                                  onMembersChange={setMembers}
                                />
                              )
                            }
                            {
                              item === "service" && (
                                <ServiceCondition
                                  targetItem={targetItem}
                                  services={services}
                                  onServicesChange={setServices}
                                  serviceCandidates={modalState.candidateServices}
                                  imitations={IMITATION}
                                  suggestion={{ values: serviceSuggestions, legendBackground: rowBackground }}
                                  disabled={modalState.isCandidateServicesLoading}
                                />
                              )
                            }
                            {
                              item === "keyword" && (
                                <KeywordCondition
                                  targetItem={targetItem}
                                  words={keywordWords}
                                  operator={keywordOperator}
                                  onWordsChange={setKeywordWords}
                                  onOperatorChange={setKeywordOperator}
                                  suggestion={{ values: keywordSuggestions, legendBackground: rowBackground }}
                                />
                              )
                            }
                            {
                              item === "url" && (
                                <UrlCondition
                                  targetItem={targetItem}
                                  words={urlWords}
                                  pattern={urlPattern}
                                  onWordsChange={setUrlWords}
                                  onPatternChange={setUrlPattern}
                                  suggestion={{ values: urlSuggestions, legendBackground: rowBackground }}
                                />
                              )
                            }
                            {
                              item === "file_path" && (
                                <FilePathCondition
                                  targetItem={targetItem}
                                  paths={filePaths}
                                  onPathsChange={setFilePaths}
                                  suggestion={{ values: filePathSuggestions, legendBackground: rowBackground }}
                                />
                              )
                            }
                          </div>
                          <button
                            type="button"
                            className="remove_button"
                            onClick={() => deleteItem(index)}
                          >
                            <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                          </button>
                        </div>
                      </React.Fragment>
                    )
                  })
                }
                {
                  selectListItems.length < availableSelectList.length && (
                    <div className="add_condition">
                      <button
                        type="button"
                        onClick={() => setIsDisplayTypeSelect(true)}
                      >
                        <FontAwesomeIcon icon={['fas', 'plus']} fixedWidth />
                        条件を追加
                      </button>
                    </div>
                  )
                }
              </div>
            </div>
            {
              !!labelingConditionDetailErrors.length && (
                <div className="condition_errors">
                  {
                    labelingConditionDetailErrors.map((v) => (
                      <div key={`${v}`}>
                        {v}
                      </div>
                    ))
                  }
                </div>
              )
            }
          </div>
          {
            previewTasks && (
              <div className="item">
                <div className="item_header">
                  <div className="title">
                    {`当日付の未仕分け作業データ(内、入力中の仕分け条件に合致する作業データ ${matchedPreviewCount}件)`}
                  </div>
                </div>
                <div className="detail row_1">
                  <AutoLabelingTargetPreview
                    previewTasks={previewTasks}
                    services={services.map((v) => v.name)}
                    keywordWords={keywordWords}
                    keywordOperator={keywordOperator}
                    filePaths={filePaths}
                    urlWords={urlWords}
                    urlPattern={urlPattern}
                    onMatchedIdsChange={(ids) => {
                      matchedPreviewTaskIdsRef.current = ids
                      setMatchedPreviewCount(ids.length)
                    }}
                  />
                </div>
              </div>
            )
          }
        </>
        <>
          <button
            type="button"
            className="button cancel"
            onClick={onCancelCallback}
          >
            キャンセル
          </button>
          <Tips
            description={isConditionLoading ? "読み込み中です" : ""}
            className=""
            icon={false}
            style={{ width: "49%", position: "relative" }}
          >
            <button
              type="button"
              className="button"
              onClick={submit}
              disabled={isSubmitting || hasValidationError() || isConditionLoading}
              style={isConditionLoading ? { width: "100%", pointerEvents: "none" } : undefined}
            >
              {mode === 'edit' ? "編集する" : "作成する"}
            </button>
          </Tips>
        </>
      </Modal>
      <Modal
        show={isDisplayTypeSelect}
        wrapperClass="labeling_condition_modal"
        closeCallback={() => setIsDisplayTypeSelect(false)}
        closeable
      >
        <>
          <div className="type_select_title">
            自動仕分け条件の追加
          </div>
          <div className="type_select_list">
            {
              availableSelectList.map((v) => (
                <div
                  key={v.value}
                  className="type_select_card"
                  onClick={() => !selectListItems.includes(v.value) && addSelectItem(v.value)}
                  role="none"
                  style={{
                    backgroundColor: selectListItems.includes(v.value) ? "#CCCCCC" : v.color,
                    cursor: selectListItems.includes(v.value) ? "not-allowed" : "pointer",
                  }}
                >
                  <div className="icon">
                    <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                  </div>
                  <div className="label">
                    {v.label}
                  </div>
                </div>
              ))
            }
          </div>
        </>
      </Modal>
      {
        isDisplayTargetSelect && (
          <AddLabelBigModal
            title="仕分け先を選択"
            labelGroupOriginList={labelGroupOriginList}
            currentMemberId={login.current_member_id || 0}
            showRecentlyUsed={isMemberVariant && selectedMember?.id === login.current_member_id}
            taskDetails={[]}
            closeCallback={() => setIsDisplayTargetSelect(false)}
            onSelectTarget={(labelGroup, label) => {
              setLabelingTarget({
                labelGroupId: labelGroup.id,
                labelGroupName: labelGroup.name,
                labelGroupCode: labelGroup.code,
                labelGroupColor: labelGroup.color,
                labelId: label.id,
                labelName: label.name,
                labelCode: label.code,
                labelColor: label.color,
              })
              setLabelingTargetErrors([])
              setIsDisplayTargetSelect(false)
            }}
            selectModeSearch={
              isMemberVariant
                ? (keywords, onSuccess) => {
                  if (!selectedMember) {
                    onSuccess([])
                    return
                  }
                  dispatch(fetchTaskSearchLabelGroup(
                    { member_id: selectedMember.id, keywords },
                    (result) => onSuccess(result.label_groups),
                  ))
                }
                : (keywords, onSuccess) => {
                  dispatch(fetchTaskSearchLabelGroup(
                    { keywords },
                    (result) => onSuccess(result.label_groups),
                  ))
                }
            }
          />
        )
      }
    </>
  )
}

export default LabelingConditionModal
